import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { UseQueryResult, useQueryClient } from "@tanstack/react-query";
import { FeatureFlags } from "@utils/FeatureFlags/generated-flags";
import { decideClientListImportAction, isOrgEhrUsable } from "@utils/ehrActions";
import { buildTemplateSelectItems, getSmartDefaultTemplate } from "@utils/helpers";
import { isReportCompleted } from "@utils/reportStatus";
import { REPORT_TYPE_KEYS, unwrapConnection } from "@utils/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  Button,
  Dialog,
  Label,
  Paragraph,
  Select,
  SelectItem,
  Small,
  Tooltip,
} from "@fronterahealth/frontera-ui-components";

import {
  ApiReportReportTypeChoices,
  ApiReportTemplateReportTypeChoices,
  EhrConnectionStatus,
  GetProviderLearnersQuery,
  GetReportsQuery,
  ReportTemplateStatus,
  ReportTemplateTypeEdge,
  ReportTypeEnums,
  useLearnerByIdQuery,
  useReportTemplatesQuery,
  useStartLearnerFileValidationMutation,
} from "@api/graphql/types-and-hooks";
import { ClientCombobox } from "@components/ClinicalNewReportDialog/ClientCombobox";
import { EHRLoginDialog } from "@components/ClinicalNewReportDialog/EHRLoginDialog";
import { Loader } from "@components/ClinicalNewReportDialog/Loader";
import { SelectTemplate } from "@components/ClinicalNewReportDialog/SelectTemplate";
import { UploadABCIntakeFile } from "@components/CreateNewClientDialog/UploadABCIntakeFile";
import { UploadBhtiaFile } from "@components/CreateNewClientDialog/UploadBhtiaFile";
import { EhrSyncGatedNotice } from "@components/EHRConnect/EhrSyncGatedNotice";
import { SegmentedTabs } from "@components/SegmentedTabs/SegmentedTabs";
import SelectableCardList from "@components/SelectableCardList/SelectableCardList";
import { useClientListImport } from "@hooks/useClientListImport";
import { useEhrAnalytics } from "@hooks/useEhrAnalytics";
import { useInFlightOnce } from "@hooks/useInFlightOnce";
import { useAdminData } from "@providers/AdminDataProvider";
import { useEHRClientData } from "@providers/EHRClientProvider";
import { useEHRConnection } from "@providers/EHRConnectionProvider";

export interface ClinicalNewReportDialogProps {
  setNewReportDialogOpen: (open: boolean) => void;
  isNewReportDialogOpen: boolean;
  setSelectedReportType: (type: ReportTypeEnums | null) => void;
  selectedReportType: ReportTypeEnums | null;
  setSelectedClientId: (id: string | null) => void;
  selectedClientId: string | null;
  setSelectedTemplate: (val: SelectItem | null) => void;
  selectedTemplate: SelectItem | null;
  getClientReport: UseQueryResult<GetReportsQuery, unknown>;
  createReportObject: (
    fileId: string | undefined,
    learnerId?: string,
    shouldCreateLearner?: boolean,
  ) => Promise<boolean>;
  goToReport: (targetReport?: { id: string; reportType: ApiReportReportTypeChoices }) => void;
  isReportCreating: boolean;
  learnerName: string;
  setLearnerName: (name: string) => void;
  learnerQuery: UseQueryResult<GetProviderLearnersQuery, unknown>;
}

export const computeReportTypeEnabled = (
  flags: Pick<FeatureFlags, "ia-clinical-merge" | "db-single-page" | "reassessment-builder">,
  hasBcbaRole: boolean,
  hasPsychologistRole: boolean,
  createNewClientSelected: boolean,
) => ({
  initialAssessment: !!flags["ia-clinical-merge"] && hasBcbaRole,
  diagnosisEvaluation: !!flags["db-single-page"] && hasPsychologistRole,
  reassessment: !!flags["reassessment-builder"] && hasBcbaRole && !createNewClientSelected,
});

export const computeIsEhrScraperUser = ({
  isHiRasmusUser,
  isMotivityUser,
  isCentralReachUser,
  isEnsoraUser,
}: {
  isHiRasmusUser: boolean;
  isMotivityUser: boolean;
  isCentralReachUser: boolean;
  isEnsoraUser: boolean;
}) => isHiRasmusUser || isMotivityUser || isCentralReachUser || isEnsoraUser;

// Derives the EHR-related user/org capability booleans from admin metadata + LD flags.
// Kept as a module-level pure helper so the boolean-operator chains do not count against
// the component's cognitive complexity.
export const computeEhrUserFlags = (params: {
  isHiRasmusUser: boolean;
  isMotivityUser: boolean;
  isCentralReachUser: boolean;
  isEnsoraUser: boolean;
  isRethinkUser: boolean;
  isHippHealthUser: boolean;
  isPassageHealthUser: boolean;
  isDataWarehouseOrg: boolean;
  isOrgUsernamePasswordOrg: boolean;
  hippHealthSync: boolean;
}) => {
  const {
    isHiRasmusUser,
    isMotivityUser,
    isCentralReachUser,
    isEnsoraUser,
    isRethinkUser,
    isHippHealthUser,
    isPassageHealthUser,
    isDataWarehouseOrg,
    isOrgUsernamePasswordOrg,
    hippHealthSync,
  } = params;

  const isEhrScraperUser = computeIsEhrScraperUser(params);
  const isEhrIntegrationUser =
    isHiRasmusUser ||
    isMotivityUser ||
    isRethinkUser ||
    isEnsoraUser ||
    isCentralReachUser ||
    (isHippHealthUser && hippHealthSync) ||
    isPassageHealthUser;
  const isCRDataWarehouse = isCentralReachUser && isDataWarehouseOrg;
  const isApiBasedEhrUser = isPassageHealthUser || isHippHealthUser;

  return {
    isEhrScraperUser,
    isEhrIntegrationUser,
    isCRDataWarehouse,
    isApiBasedEhrUser,
    // Org-level admin account: org-managed credentials, no per-provider login.
    isOrgUsernamePassword: isOrgUsernamePasswordOrg,
  };
};

// Maps the UI report-type enum to the API template-type enum.
export const getApiReportTemplateType = (reportType: ReportTypeEnums): ApiReportTemplateReportTypeChoices => {
  switch (reportType) {
    case ReportTypeEnums.InitialAssessment:
      return ApiReportTemplateReportTypeChoices.InitialAssessment;
    case ReportTypeEnums.DiagnosisEvaluation:
      return ApiReportTemplateReportTypeChoices.DiagnosisEvaluation;
    case ReportTypeEnums.Reassessment:
      return ApiReportTemplateReportTypeChoices.Reassessment;
    default:
      return ApiReportTemplateReportTypeChoices.InitialAssessment;
  }
};

export const reportTypeSupportsTemplateSelector = (selectedReportType: ReportTypeEnums | null): boolean =>
  selectedReportType === ReportTypeEnums.Reassessment ||
  selectedReportType === ReportTypeEnums.DiagnosisEvaluation ||
  selectedReportType === ReportTypeEnums.InitialAssessment;

type GetReportsEdgeNode = NonNullable<NonNullable<NonNullable<GetReportsQuery["getReports"]>["edges"][number]>["node"]>;

// Finds a still-open (non-completed, non-archived) reassessment for the selected client, if any.
// Drives a warning — never a block — when the user starts a reassessment while one is already in
// progress (PROA-1947). Returns null unless the selected type is Reassessment, so callers can pass
// it through unconditionally. "Open" is the inverse of the terminal Completed state, so Draft,
// Generating, AwaitingGoalSelection and Failure all count.
export const getExistingOpenReassessment = (
  clientReportData: GetReportsQuery | undefined,
  selectedReportType: ReportTypeEnums | null,
): GetReportsEdgeNode | null => {
  if (selectedReportType !== ReportTypeEnums.Reassessment) return null;
  const openReassessment = clientReportData?.getReports?.edges
    ?.map((edge) => edge?.node)
    .find(
      (node) =>
        !!node &&
        (node.reportType as unknown as ReportTypeEnums) === ReportTypeEnums.Reassessment &&
        !node.isArchive &&
        !isReportCompleted(node),
    );
  return openReassessment ?? null;
};

// Dialog title varies by which clinical roles the user holds.
export const computeDialogTitle = (params: {
  hasPsychologistRole: boolean;
  hasBcbaRole: boolean;
  isIaClinicalMerge: boolean;
}): string => {
  const { hasPsychologistRole, hasBcbaRole, isIaClinicalMerge } = params;
  if (hasPsychologistRole && !hasBcbaRole) {
    return "New Evaluation";
  }
  if (hasBcbaRole && !hasPsychologistRole) {
    return isIaClinicalMerge ? "New Clinical Report" : "New Reassessment";
  }
  return "New Clinical Report";
};

// Whether the user may currently submit a new report. Pure aggregation of all gating
// conditions; extracted so its long boolean chain stays out of the component body.
export const computeReportCreationDisabled = (params: {
  isLearnersLoading: boolean;
  clientSelected: boolean;
  selectedTypeIsCreatable: boolean;
  selectedReportType: ReportTypeEnums | null;
  clientHasSelectedReport: boolean;
  isDemoOrgMode: boolean;
  abcIntakeFileRequired: boolean;
  bhtiaFileIsProcessing: boolean;
  templateRequired: boolean;
  integratedButMissingLink: boolean;
  isReportCreating: boolean;
}): boolean => {
  const {
    isLearnersLoading,
    clientSelected,
    selectedTypeIsCreatable,
    selectedReportType,
    clientHasSelectedReport,
    isDemoOrgMode,
    abcIntakeFileRequired,
    bhtiaFileIsProcessing,
    templateRequired,
    integratedButMissingLink,
    isReportCreating,
  } = params;
  const existingReportBlocks =
    selectedReportType !== ReportTypeEnums.Reassessment && clientHasSelectedReport && !isDemoOrgMode;
  return (
    isLearnersLoading ||
    !clientSelected ||
    !selectedTypeIsCreatable ||
    existingReportBlocks ||
    abcIntakeFileRequired ||
    bhtiaFileIsProcessing ||
    templateRequired ||
    integratedButMissingLink ||
    isReportCreating
  );
};

type ContinueAction =
  | { kind: "de-intake"; learnerFileId: string | undefined; learnerId: string }
  | { kind: "ia-intake"; learnerFileId: string | undefined; learnerId: string }
  | { kind: "default"; shouldCreateSparseLearner: boolean };

export const getIaCardDescription = ({
  isIaIntakeUpload,
  createNewClientSelected,
  isCatalightUser,
}: {
  isIaIntakeUpload: boolean;
  createNewClientSelected: boolean;
  isCatalightUser: boolean;
}): string => {
  if (isIaIntakeUpload && createNewClientSelected) {
    return isCatalightUser
      ? "Use this to create a comprehensive initial assessment for a new client beginning ABA services. Upload a BHTIA file to prefill the client's details automatically, or enter them manually. Includes intake data, assessment scores, and a full treatment plan."
      : "Use this to create a comprehensive initial assessment for a new client beginning ABA services. Upload an intake document to prefill the client's details automatically, or enter them manually. Includes intake data, assessment scores, and a full treatment plan.";
  }
  return "Use this to create a comprehensive initial assessment for a new client beginning ABA services. Includes intake data, assessment scores, and a full treatment plan.";
};

// Auto-select report type based on user roles when a client is selected.
// Only auto-selects when the choice is unambiguous (exactly one enabled option,
// or a single-role shortcut). Returns null when no auto-selection should happen.
export const resolveAutoSelectReportType = (params: {
  enabledOptionIds: ReportTypeEnums[];
  isDbSinglePageEnabled: boolean;
  isReassessmentBuilder: boolean;
  hasPsychologistRole: boolean;
  hasBcbaRole: boolean;
  createNewClientSelected: boolean;
}): ReportTypeEnums | null => {
  const {
    enabledOptionIds,
    isDbSinglePageEnabled,
    isReassessmentBuilder,
    hasPsychologistRole,
    hasBcbaRole,
    createNewClientSelected,
  } = params;
  if (enabledOptionIds.length === 1) {
    return enabledOptionIds[0];
  }
  if (isDbSinglePageEnabled && hasPsychologistRole && !hasBcbaRole) {
    return ReportTypeEnums.DiagnosisEvaluation;
  }
  if (isReassessmentBuilder && hasBcbaRole && !hasPsychologistRole && !createNewClientSelected) {
    return ReportTypeEnums.Reassessment;
  }
  return null;
};

export const resolveContinueAction = (params: {
  selectedReportType: ReportTypeEnums | null;
  createNewClientSelected: boolean;
  abcIntakeLearnerId: string | undefined;
  abcIntakeLearnerFileId: string | undefined;
  bhtiaLearnerId: string | undefined;
  bhtiaLearnerFileId: string | undefined;
  iaIntakeMode: "upload" | "manual";
  dbSinglepageBulkupload: boolean;
}): ContinueAction => {
  const {
    selectedReportType,
    createNewClientSelected,
    abcIntakeLearnerId,
    abcIntakeLearnerFileId,
    bhtiaLearnerId,
    bhtiaLearnerFileId,
    iaIntakeMode,
    dbSinglepageBulkupload,
  } = params;

  if (selectedReportType === ReportTypeEnums.DiagnosisEvaluation && createNewClientSelected && abcIntakeLearnerId) {
    return { kind: "de-intake", learnerFileId: abcIntakeLearnerFileId, learnerId: abcIntakeLearnerId };
  }
  // iaIntakeMode is authoritative: in "manual" mode never take the BHTSF path even if a late,
  // in-flight upload callback re-set bhtiaLearnerId after the user toggled to manual.
  if (
    selectedReportType === ReportTypeEnums.InitialAssessment &&
    createNewClientSelected &&
    iaIntakeMode === "upload" &&
    bhtiaLearnerId
  ) {
    return { kind: "ia-intake", learnerFileId: bhtiaLearnerFileId, learnerId: bhtiaLearnerId };
  }
  // Sparse learner is only required for the single-page DE bulk-upload flow, whose next page
  // auto-attaches uploads to a learner. IA/Reassessment/legacy DE break if we pre-create one.
  const shouldCreateSparseLearner =
    createNewClientSelected && selectedReportType === ReportTypeEnums.DiagnosisEvaluation && dbSinglepageBulkupload;
  return { kind: "default", shouldCreateSparseLearner };
};

// Amber "data-integrity" warning banner — matches the styling introduced in PR #3285 so the
// "report already exists" (IA/DE) and "reassessment in progress" notices read consistently.
// Purely presentational; whether creation is blocked is decided elsewhere (computeReportCreationDisabled).
const bannerLinkClassName = "cursor-pointer font-semibold underline";

const WarningBanner: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mb-4 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 p-3">
    <ExclamationCircleIcon className="h-5 w-5 shrink-0 text-amber-500" />
    <p className="text-sm text-amber-800">{children}</p>
  </div>
);

// Card body for a report-type option: a "Loading"/"already exists" message when the
// client already has this report type, otherwise the template picker for that type.
const ReportTypeOptionContent: React.FC<{
  reportType: ReportTypeEnums;
  isClientReportLoading: boolean;
  clientHasSelectedReport: boolean;
  goToReport: () => void;
  selectedReportType: ReportTypeEnums | null;
  selectedTemplate: SelectItem | null;
  setSelectedTemplate: (val: SelectItem | null) => void;
  selectedClientId: string | null;
}> = ({
  reportType,
  isClientReportLoading,
  clientHasSelectedReport,
  goToReport,
  selectedReportType,
  selectedTemplate,
  setSelectedTemplate,
  selectedClientId,
}) => {
  if (isClientReportLoading) {
    return <Small>Loading...</Small>;
  }
  if (clientHasSelectedReport) {
    return (
      <WarningBanner>
        An {REPORT_TYPE_KEYS[reportType]} for this client already exists.{" "}
        <button type="button" className={bannerLinkClassName} onClick={() => goToReport()}>
          View draft
        </button>
      </WarningBanner>
    );
  }
  return (
    <SelectTemplate
      reportType={reportType}
      preselectTemplate={selectedReportType === reportType}
      selectedTemplate={selectedTemplate}
      setSelectedTemplate={setSelectedTemplate}
      learnerId={selectedClientId}
    />
  );
};

// Non-blocking warning shown inside the Reassessment card when the client already has an
// open (in-progress) reassessment. Unlike the IA "already exists" state, this never disables
// Continue — the user may still create another (PROA-1947).
const ReassessmentInProgressWarning: React.FC<{ onViewExisting: () => void }> = ({ onViewExisting }) => (
  <WarningBanner>
    This client already has a reassessment in progress.{" "}
    <button type="button" className={bannerLinkClassName} onClick={onViewExisting}>
      View draft
    </button>{" "}
    — you can still continue creating a new one.
  </WarningBanner>
);

interface ReportTypeOption {
  id: ReportTypeEnums;
  title: string;
  description: string;
  isSelected: boolean;
  enabled: boolean;
  content: React.ReactNode;
}

// Builds the three report-type cards. Pure aside from the React nodes it composes;
// all branching that selects "already exists" vs intake-upload vs default content
// lives here so the component body stays flat.
const buildReportTypeOptions = (params: {
  reportTypeEnabled: ReturnType<typeof computeReportTypeEnabled>;
  hasPsychologistRole: boolean;
  hasBcbaRole: boolean;
  isDemoOrgMode: boolean;
  isDbIntakeUpload: boolean;
  isIaIntakeUpload: boolean;
  isCatalightUser: boolean;
  createNewClientSelected: boolean;
  clientHasSelectedReport: boolean;
  selectedReportType: ReportTypeEnums | null;
  renderExistingReportContent: (reportType: ReportTypeEnums) => React.ReactNode;
  iaIntakeCardContent: React.ReactNode;
  abcIntakeCardContent: React.ReactNode;
  reassessmentCardContent: React.ReactNode;
}): ReportTypeOption[] => {
  const {
    reportTypeEnabled,
    hasPsychologistRole,
    hasBcbaRole,
    isDemoOrgMode,
    isDbIntakeUpload,
    isIaIntakeUpload,
    isCatalightUser,
    createNewClientSelected,
    clientHasSelectedReport,
    selectedReportType,
    renderExistingReportContent,
    iaIntakeCardContent,
    abcIntakeCardContent,
    reassessmentCardContent,
  } = params;

  const showExistingFor = (reportType: ReportTypeEnums) =>
    clientHasSelectedReport && !isDemoOrgMode && selectedReportType === reportType;

  return [
    {
      id: ReportTypeEnums.InitialAssessment,
      title: "Initial Assessment",
      description: getIaCardDescription({ isIaIntakeUpload, createNewClientSelected, isCatalightUser }),
      isSelected: false,
      enabled: reportTypeEnabled.initialAssessment,
      // Only show card content when IA is selected and the client already has one —
      // guards against clientHasSelectedReport being true for a different selected type
      // which would incorrectly show the "already exists" message inside the IA card.
      // When no existing report, content is the intake card (or undefined so the
      // template selector renders below the cards).
      content: showExistingFor(ReportTypeEnums.InitialAssessment)
        ? renderExistingReportContent(ReportTypeEnums.InitialAssessment)
        : iaIntakeCardContent,
    },
    {
      id: ReportTypeEnums.DiagnosisEvaluation,
      title: hasPsychologistRole && hasBcbaRole ? "Diagnosis Evaluation" : "",
      description:
        isDbIntakeUpload && createNewClientSelected
          ? "Use this to complete a diagnosis evaluation for a new client. Select a report template and upload your Intake Form to get started."
          : "Use this to complete a diagnosis evaluation for a client. Select a report template to get started.",
      isSelected: false,
      enabled: reportTypeEnabled.diagnosisEvaluation,
      content: showExistingFor(ReportTypeEnums.DiagnosisEvaluation)
        ? renderExistingReportContent(ReportTypeEnums.DiagnosisEvaluation)
        : abcIntakeCardContent,
    },
    {
      id: ReportTypeEnums.Reassessment,
      title: "Reassessment",
      description:
        "Use this to update an existing client's progress and treatment plan. Includes goal reviews, updated assessments, and justification for continued care.",
      isSelected: false,
      enabled: reportTypeEnabled.reassessment,
      content: reassessmentCardContent,
    },
  ];
};

// Static intro + client/template selectors for the dialog. Extracted to module level
// so its conditional JSX does not contribute to the component's cognitive complexity.
const DefaultDialogContent: React.FC<{
  isEhrIntegrationUser: boolean;
  isImporting: boolean;
  checkLoginStateBeforeClientListImport: () => void;
  isCRDataWarehouse: boolean;
  ehrConnectionStatus: EhrConnectionStatus | null | undefined;
  isOrgUsernamePassword: boolean;
  isEhrSyncCleared: boolean;
  createNewClientSelected: boolean;
  selectedClientId: string | null;
  onSelectClientId: (val: string | null) => void;
  onIntegratedButMissingLinkChange: (value: boolean) => void;
  onCreateNewClient: () => void;
  showCardList: boolean;
  enabledReportTypeOptions: ReportTypeOption[];
  selectedReportType: ReportTypeEnums | null;
  onReportTypeSelect: (id: ReportTypeEnums) => void;
  isClientReportLoading: boolean;
  clientHasSelectedReport: boolean;
  isDemoOrgMode: boolean;
  goToReport: () => void;
  showTemplateSelector: boolean;
  templatesLoading: boolean;
  templateSelections: SelectItem[];
  selectedTemplate: SelectItem | null;
  setSelectedTemplate: (val: SelectItem | null) => void;
}> = ({
  isEhrIntegrationUser,
  isImporting,
  checkLoginStateBeforeClientListImport,
  isCRDataWarehouse,
  ehrConnectionStatus,
  isOrgUsernamePassword,
  isEhrSyncCleared,
  createNewClientSelected,
  selectedClientId,
  onSelectClientId,
  onIntegratedButMissingLinkChange,
  onCreateNewClient,
  showCardList,
  enabledReportTypeOptions,
  selectedReportType,
  onReportTypeSelect,
  isClientReportLoading,
  clientHasSelectedReport,
  isDemoOrgMode,
  goToReport,
  showTemplateSelector,
  templatesLoading,
  templateSelections,
  selectedTemplate,
  setSelectedTemplate,
}) => {
  const cardListSection =
    (selectedClientId || createNewClientSelected) && enabledReportTypeOptions.length >= 1 ? (
      <div>
        <SelectableCardList
          options={enabledReportTypeOptions}
          selectedOptionId={selectedReportType}
          onSelect={(id) => onReportTypeSelect(id as ReportTypeEnums)}
          name="CatalightClientCreationOptions"
        />
      </div>
    ) : null;
  return (
    <>
      <div className="flex flex-col gap-4 ">
        <Paragraph size="small" className="mb-4">
          Create a report for a client. You can start from a base report template or select an assessment template set
          up by your organization.
        </Paragraph>
        {isEhrIntegrationUser && (
          <div className="flex flex-col">
            <Paragraph size="small">
              If you don’t see the client you’re looking for, your client list may be out of date.
            </Paragraph>
            {isImporting ? (
              <div className="flex flex-row gap-2 mt-4 items-center">
                <Loader className="h-5 w-5 ml-0 mr-0" />
                <Paragraph>Refreshing your client list...</Paragraph>
              </div>
            ) : (
              <Button
                text="Refresh Client List"
                appearance="text"
                onClick={checkLoginStateBeforeClientListImport}
                className="-ml-3.5 disabled:text-limestone-400 disabled:opacity-100"
                disabled={
                  !isEhrSyncCleared ||
                  (isCRDataWarehouse && ehrConnectionStatus !== EhrConnectionStatus.Connected) ||
                  // Org-admin: only disable when the org EHR is genuinely not set up;
                  // connected, active/in-progress, and CONNECTION_ERROR all allow a retry.
                  (isOrgUsernamePassword && !isOrgEhrUsable(ehrConnectionStatus))
                }
              />
            )}
            {!isEhrSyncCleared && <EhrSyncGatedNotice />}
            <br />
          </div>
        )}
      </div>
      <ClientCombobox
        selectedClientId={selectedClientId}
        createNewClientSelected={createNewClientSelected}
        onSelect={onSelectClientId}
        onCreateNew={onCreateNewClient}
        onIntegratedButMissingLinkChange={onIntegratedButMissingLinkChange}
        className="mb-4"
      />
      {showCardList ? (
        cardListSection
      ) : (
        <>
          {isClientReportLoading && <Small>Loading...</Small>}
          {clientHasSelectedReport && !isDemoOrgMode && (
            <WarningBanner>
              A report for this client already exists.{" "}
              <button type="button" className={bannerLinkClassName} onClick={() => goToReport()}>
                View draft
              </button>
            </WarningBanner>
          )}
        </>
      )}

      {/* Template Selection Dropdown */}
      {showTemplateSelector && (
        <Select
          title="Select Template"
          isLoading={templatesLoading}
          items={templateSelections}
          placeholderText="Type to find a template"
          selected={selectedTemplate}
          setSelected={setSelectedTemplate}
          enableSearch
        />
      )}
    </>
  );
};

// IA-intake card body: a BHTSF/intake upload vs manual-entry toggle. Extracted so its
// nested ternary and toggle handler don't count against the component's complexity.
const IaIntakeUploadCard: React.FC<{
  isCatalightUser: boolean;
  iaIntakeMode: "upload" | "manual";
  setIaIntakeMode: (mode: "upload" | "manual") => void;
  resetBhtiaState: () => void;
  setBhtiaLearnerId: (id: string | undefined) => void;
  setBhtiaLearnerFileId: (id: string | undefined) => void;
  setBhtiaFileIsProcessing: (processing: boolean) => void;
}> = ({
  isCatalightUser,
  iaIntakeMode,
  setIaIntakeMode,
  resetBhtiaState,
  setBhtiaLearnerId,
  setBhtiaLearnerFileId,
  setBhtiaFileIsProcessing,
}) => (
  <div className="flex flex-col gap-3" onClick={(e) => e.stopPropagation()}>
    <SegmentedTabs<"upload" | "manual">
      className="w-fit"
      tabs={[
        { value: "upload", label: isCatalightUser ? "Upload BHTIA file" : "Upload intake document" },
        { value: "manual", label: "Enter manually" },
      ]}
      activeTab={iaIntakeMode}
      onChange={(mode) => {
        if (mode === "manual") {
          resetBhtiaState();
        }
        setIaIntakeMode(mode);
      }}
    />
    {iaIntakeMode === "upload" ? (
      <UploadBhtiaFile
        setLearnerId={setBhtiaLearnerId}
        disabled={false}
        fileUploadedCallback={() => {
          setBhtiaFileIsProcessing(false);
        }}
        setLearnerFileId={setBhtiaLearnerFileId}
        onFileRemoved={resetBhtiaState}
        onFileUploadStarted={() => setBhtiaFileIsProcessing(true)}
        onUploadError={resetBhtiaState}
      />
    ) : (
      <Paragraph size="small" colorType="secondary">
        You&apos;ll enter client details on the next step.
      </Paragraph>
    )}
  </div>
);

// DE-intake card body: intake-document upload.
const AbcIntakeUploadCard: React.FC<{
  setAbcIntakeLearnerId: (id: string | undefined) => void;
  setAbcIntakeLearnerFileId: (id: string | undefined) => void;
  setAbcIntakeFileUploaded: (uploaded: boolean) => void;
  setAbcIntakeFileIsProcessing: (processing: boolean) => void;
  resetAbcIntakeState: () => void;
}> = ({
  setAbcIntakeLearnerId,
  setAbcIntakeLearnerFileId,
  setAbcIntakeFileUploaded,
  setAbcIntakeFileIsProcessing,
  resetAbcIntakeState,
}) => (
  <div onClick={(e) => e.stopPropagation()}>
    <Label className="text-text-primary">Upload Intake Document</Label>
    <UploadABCIntakeFile
      setLearnerId={setAbcIntakeLearnerId}
      disabled={false}
      fileUploadedCallback={() => {
        setAbcIntakeFileUploaded(true);
        setAbcIntakeFileIsProcessing(false);
      }}
      setLearnerFileId={setAbcIntakeLearnerFileId}
      onFileRemoved={resetAbcIntakeState}
      onFileUploadStarted={() => setAbcIntakeFileIsProcessing(true)}
    />
  </div>
);

// Dispatches a resolved continue action to the create-report side effects. Settles only once
// the create has finished, so the in-flight guard on Continue holds until then, and resolves
// whether a report was created (PROA-3064).
export const executeContinueAction = async (
  action: ContinueAction,
  deps: {
    validateLearnerFile: (learnerFileId: string) => Promise<unknown>;
    createReportObject: (
      fileId: string | undefined,
      learnerId?: string,
      shouldCreateLearner?: boolean,
    ) => Promise<boolean>;
  },
): Promise<boolean> => {
  if (action.kind === "de-intake") {
    if (action.learnerFileId) {
      await deps.validateLearnerFile(action.learnerFileId);
    }
    return deps.createReportObject(action.learnerFileId, action.learnerId);
  }
  if (action.kind === "ia-intake") {
    return deps.createReportObject(action.learnerFileId, action.learnerId);
  }
  return deps.createReportObject(undefined, undefined, action.shouldCreateSparseLearner);
};

export const ClinicalNewReportDialog: React.FC<ClinicalNewReportDialogProps> = ({
  isNewReportDialogOpen,
  setNewReportDialogOpen,
  selectedClientId,
  setSelectedClientId,
  setSelectedReportType,
  selectedReportType,
  selectedTemplate,
  setSelectedTemplate,
  getClientReport: { data: clientReportData, isLoading: isClientReportLoading },
  createReportObject,
  goToReport,
  isReportCreating,
  learnerQuery,
}) => {
  const flags = useFlags();
  const [createNewClientSelected, setCreateNewClientSelected] = useState<boolean>(false);
  // Lifted from ClientCombobox (which owns the learner list); gates the Continue button.
  const [integratedButMissingLink, setIntegratedButMissingLink] = useState<boolean>(false);

  const { isLoading: isLearnersLoading } = learnerQuery;
  const {
    isHiRasmusUser,
    isMotivityUser,
    isCentralReachUser,
    isCatalightUser,
    roles,
    isHippHealthUser,
    isPassageHealthUser,
    isRethinkUser,
    isEnsoraUser,
    isDataWarehouseOrg,
    isOrgUsernamePasswordOrg,
    isUserEnterpriseAdmin,
    isEhrSyncCleared,
  } = useAdminData();
  const { setUploadedFileMeta, setIsClientImportedFromEHR } = useEHRClientData();
  const { hasCredentials, usesOrgSession } = useEHRConnection();
  const queryClient = useQueryClient();
  const [isEHRLogin, setIsEHRLogin] = useState<boolean>(false);
  const [abcIntakeLearnerId, setAbcIntakeLearnerId] = useState<string | undefined>(undefined);
  const [abcIntakeLearnerFileId, setAbcIntakeLearnerFileId] = useState<string | undefined>(undefined);
  const [abcIntakeFileUploaded, setAbcIntakeFileUploaded] = useState<boolean>(false);
  const [abcIntakeFileIsProcessing, setAbcIntakeFileIsProcessing] = useState<boolean>(false);

  const [bhtiaLearnerId, setBhtiaLearnerId] = useState<string | undefined>(undefined);
  const [bhtiaLearnerFileId, setBhtiaLearnerFileId] = useState<string | undefined>(undefined);
  const [bhtiaFileIsProcessing, setBhtiaFileIsProcessing] = useState<boolean>(false);
  const [iaIntakeMode, setIaIntakeMode] = useState<"upload" | "manual">("upload");
  // Set once Continue creates a report; the dialog closes a render or two later (PROA-3064).
  const reportCreatedRef = useRef(false);

  const resetBhtiaState = () => {
    setBhtiaLearnerId(undefined);
    setBhtiaLearnerFileId(undefined);
    setBhtiaFileIsProcessing(false);
  };

  const startLearnerFileValidation = useStartLearnerFileValidationMutation();

  const isDemoOrgMode = !!flags["demo-org-mode"];
  const { isEhrScraperUser, isEhrIntegrationUser, isCRDataWarehouse, isApiBasedEhrUser, isOrgUsernamePassword } =
    computeEhrUserFlags({
      isHiRasmusUser,
      isMotivityUser,
      isCentralReachUser,
      isEnsoraUser,
      isRethinkUser,
      isHippHealthUser,
      isPassageHealthUser,
      isDataWarehouseOrg,
      isOrgUsernamePasswordOrg,
      hippHealthSync: !!flags["hipphealth-sync"],
    });

  const { isImporting, startImport, cancelImport, ehrConnectionStatus } = useClientListImport({
    // INT-36: CentralReach orgs on shared credentials queue the import
    // on the org-wide session instead of signalling a per-provider
    // workflow, and completion is read off the request row.
    usesOrgSession,
    enabled: isEhrIntegrationUser && isNewReportDialogOpen,
    waitForStatusTransition: !isApiBasedEhrUser,
    isEhrSyncCleared,
    onSuccess: () => {
      // The client picker owns the learner query; invalidate by base key so its list refreshes.
      queryClient.invalidateQueries({ queryKey: ["getProviderLearners"] });
    },
    onLoginFailed: () => {
      // Org-level admin account: no per-provider login dialog to surface. Refresh
      // the connection so the credential failure shows on the top EHR badge (the
      // hook suppresses its error toast when an onLoginFailed handler is set),
      // matching ClientDashboard. Other orgs open the per-provider login dialog.
      if (isOrgUsernamePassword) {
        queryClient.invalidateQueries({ queryKey: ["ehrConnection"] });
      } else {
        setIsEHRLogin(true);
      }
    },
  });
  const { captureEhrRefreshClicked } = useEhrAnalytics();

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (isNewReportDialogOpen) {
      resetAbcIntakeState();
      resetBhtiaState();
      setIaIntakeMode("upload");
      reportCreatedRef.current = false;
    } else {
      cancelImport();
    }
  }, [isNewReportDialogOpen, cancelImport]);

  const resetAbcIntakeState = () => {
    setAbcIntakeLearnerId(undefined);
    setAbcIntakeLearnerFileId(undefined);
    setAbcIntakeFileUploaded(false);
    setAbcIntakeFileIsProcessing(false);
  };

  const handleSecondaryButtonClick = () => {
    setNewReportDialogOpen(false);
    setSelectedReportType(null);
    resetAbcIntakeState();
    resetBhtiaState();
    setIaIntakeMode("upload");
  };

  const clientHasSelectedReport =
    !isClientReportLoading &&
    !!clientReportData?.getReports?.edges.find(
      (edge) => (edge?.node?.reportType as unknown as ReportTypeEnums) === selectedReportType,
    );

  const handleReportTypeSelect = (id: ReportTypeEnums) => {
    setSelectedTemplate(null);
    setSelectedReportType(id);
  };

  const templateStatuses = isUserEnterpriseAdmin
    ? [ReportTemplateStatus.Published, ReportTemplateStatus.Draft]
    : [ReportTemplateStatus.Published];

  const { data: templatesData, isLoading: templatesLoading } = useReportTemplatesQuery(
    {
      filter: {
        reportType: selectedReportType
          ? getApiReportTemplateType(selectedReportType)
          : ApiReportTemplateReportTypeChoices.InitialAssessment,
        statuses: templateStatuses,
        // Narrowed server-side to the selected client's payor, with a fallback to
        // every org template when that payor has none (PROA-2154).
        learner: selectedClientId ?? undefined,
      },
    },
    {
      queryKey: ["get-report-templates", selectedReportType, isUserEnterpriseAdmin, selectedClientId],
      enabled: !!selectedReportType,
    },
  );

  const { data: selectedClientData } = useLearnerByIdQuery(
    { learnerId: selectedClientId ?? "" },
    {
      queryKey: ["learner-by-id", selectedClientId],
      enabled: !!selectedClientId && !createNewClientSelected,
      staleTime: Infinity,
    },
  );

  const selectedClientPrimaryPayor = unwrapConnection(selectedClientData?.learnerById?.learnerpayorSet)
    ?.filter((p) => p.isActive)
    .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())[0];
  const clientPayorType = selectedClientPrimaryPayor?.payorType ?? null;

  const templateEdges = templatesData?.getReportTemplates?.edges;

  const templateSelections = useMemo(
    () => buildTemplateSelectItems((templateEdges ?? []) as ReportTemplateTypeEdge[], isDemoOrgMode),
    [templateEdges, isDemoOrgMode],
  );

  // Tracks the id of the template this effect last auto-selected. Gating the
  // payor-match branch on `!selectedTemplate` alone meant it only ever ran
  // once — if templateSelections resolved before clientPayorType (or the
  // client changed after a template was already auto-assigned), a matching
  // payor template was silently skipped forever. Comparing against this ref
  // instead lets the effect keep correcting its own auto-selection as
  // `clientPayorType` arrives/changes, while never touching a selection the
  // user picked manually (which won't match the ref).
  const lastAutoSelectedTemplateIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!selectedReportType) {
      setSelectedTemplate(null);
      lastAutoSelectedTemplateIdRef.current = null;
      return;
    }
    if (templateSelections.length === 0) return;

    const selectionIsOurs = !selectedTemplate || selectedTemplate.id === lastAutoSelectedTemplateIdRef.current;
    if (!selectionIsOurs) return;

    // Prefer a template whose payorType matches the client's assigned payor
    const payorMatch = clientPayorType
      ? templateSelections.find((t) => (t.payorType as string) === (clientPayorType as string)) ?? null
      : null;
    const smartDefault = getSmartDefaultTemplate(templateSelections);
    const nextTemplate = payorMatch || smartDefault || templateSelections[0];

    if (nextTemplate?.id !== selectedTemplate?.id) {
      setSelectedTemplate(nextTemplate);
    }
    lastAutoSelectedTemplateIdRef.current = nextTemplate?.id ?? null;
  }, [selectedReportType, templateSelections, selectedTemplate, setSelectedTemplate, clientPayorType]);

  const showTemplateSelector = reportTypeSupportsTemplateSelector(selectedReportType);

  const checkLoginStateBeforeClientListImport = () => {
    captureEhrRefreshClicked("new_report_modal");
    const action = decideClientListImportAction({
      isApiKeyOrg: isHippHealthUser || isPassageHealthUser,
      isEhrIntegrationUser,
      isOrgUsernamePassword,
      hasCredentials,
      ehrConnectionStatus,
      isEhrSyncCleared,
    });
    if (action === "import") {
      startImport();
    } else if (action === "open-login") {
      setIsEHRLogin(true);
    }
  };

  const continueToClientListImportAfterLogin = async (loginSuccess: boolean) => {
    setIsEHRLogin(false);
    if (!isEhrIntegrationUser || !loginSuccess) return;
    await startImport();
  };

  const handleClientSelect = (id: string | null) => {
    setCreateNewClientSelected(false);
    resetBhtiaState();
    setUploadedFileMeta(null);
    setSelectedClientId(id);
    // A different client can mean a different payor, and the template list is
    // narrowed by payor server-side — so even a manual pick has to go. Keeping it
    // would submit a template the new client is not offered.
    setSelectedTemplate(null);
    lastAutoSelectedTemplateIdRef.current = null;
  };

  const handleCreateNewClient = () => {
    setSelectedClientId(null);
    setSelectedReportType(null);
    setCreateNewClientSelected(true);
    resetBhtiaState();
    setIaIntakeMode("upload");
  };

  const renderReportTypeOptionContent = (reportType: ReportTypeEnums) => (
    <ReportTypeOptionContent
      reportType={reportType}
      isClientReportLoading={isClientReportLoading}
      clientHasSelectedReport={clientHasSelectedReport}
      goToReport={goToReport}
      selectedReportType={selectedReportType}
      selectedTemplate={selectedTemplate}
      setSelectedTemplate={setSelectedTemplate}
      selectedClientId={selectedClientId}
    />
  );

  // Role-based checks for report type availability
  const hasPsychologistRole = roles.includes("psychologist") || roles.includes("evaluation-psychologist");
  const hasBcbaRole = roles.includes("assessment-bcba") || roles.includes("bcba");
  const isDbSinglePageEnabled = flags["db-single-page"];
  const isDbIntakeUpload = !!flags["db-intake-upload"];
  const isIaIntakeUpload = !!flags["ia-intake-upload"];

  const isIaClinicalMerge = !!flags["ia-clinical-merge"];
  const isReassessmentBuilder = !!flags["reassessment-builder"];

  const reportTypeEnabled = computeReportTypeEnabled(
    flags as Pick<FeatureFlags, "ia-clinical-merge" | "db-single-page" | "reassessment-builder">,
    hasBcbaRole,
    hasPsychologistRole,
    createNewClientSelected,
  );

  const dialogTitle = computeDialogTitle({ hasPsychologistRole, hasBcbaRole, isIaClinicalMerge });

  const iaIntakeCardContent =
    isIaIntakeUpload && createNewClientSelected && selectedReportType === ReportTypeEnums.InitialAssessment ? (
      <IaIntakeUploadCard
        isCatalightUser={isCatalightUser}
        iaIntakeMode={iaIntakeMode}
        setIaIntakeMode={setIaIntakeMode}
        resetBhtiaState={resetBhtiaState}
        setBhtiaLearnerId={setBhtiaLearnerId}
        setBhtiaLearnerFileId={setBhtiaLearnerFileId}
        setBhtiaFileIsProcessing={setBhtiaFileIsProcessing}
      />
    ) : undefined;

  const abcIntakeCardContent =
    isDbIntakeUpload && createNewClientSelected ? (
      <AbcIntakeUploadCard
        setAbcIntakeLearnerId={setAbcIntakeLearnerId}
        setAbcIntakeLearnerFileId={setAbcIntakeLearnerFileId}
        setAbcIntakeFileUploaded={setAbcIntakeFileUploaded}
        setAbcIntakeFileIsProcessing={setAbcIntakeFileIsProcessing}
        resetAbcIntakeState={resetAbcIntakeState}
      />
    ) : null;

  // Warn — never block — when the client already has an in-progress reassessment (PROA-1947).
  // Demo orgs skip the guard, matching the IA "already exists" behavior below.
  const existingOpenReassessment = getExistingOpenReassessment(clientReportData, selectedReportType);
  const reassessmentCardContent =
    existingOpenReassessment && !isDemoOrgMode ? (
      <ReassessmentInProgressWarning onViewExisting={() => goToReport(existingOpenReassessment)} />
    ) : null;

  const reportTypeOptions = buildReportTypeOptions({
    reportTypeEnabled,
    hasPsychologistRole,
    hasBcbaRole,
    isDemoOrgMode,
    isDbIntakeUpload,
    isIaIntakeUpload,
    isCatalightUser,
    createNewClientSelected,
    clientHasSelectedReport,
    selectedReportType,
    renderExistingReportContent: renderReportTypeOptionContent,
    iaIntakeCardContent,
    abcIntakeCardContent,
    reassessmentCardContent,
  });

  // Auto-select report type based on user roles when client is selected.
  // Only auto-selects when the choice is unambiguous (see resolveAutoSelectReportType).
  useEffect(() => {
    if ((selectedClientId || createNewClientSelected) && !selectedReportType) {
      const autoType = resolveAutoSelectReportType({
        enabledOptionIds: reportTypeOptions.filter((option) => option.enabled).map((option) => option.id),
        isDbSinglePageEnabled,
        isReassessmentBuilder,
        hasPsychologistRole,
        hasBcbaRole,
        createNewClientSelected,
      });
      if (autoType) {
        setSelectedReportType(autoType);
      }
    }
  }, [
    selectedClientId,
    createNewClientSelected,
    selectedReportType,
    hasPsychologistRole,
    hasBcbaRole,
    isDbSinglePageEnabled,
    isReassessmentBuilder,
  ]);

  useEffect(() => {
    if (selectedReportType !== ReportTypeEnums.InitialAssessment) {
      resetBhtiaState();
      setIaIntakeMode("upload");
    }
  }, [selectedReportType]);

  const clientSelected =
    (!!selectedClientId && !isClientReportLoading) ||
    (selectedReportType !== ReportTypeEnums.Reassessment && createNewClientSelected);

  const abcIntakeFileRequired =
    selectedReportType === ReportTypeEnums.DiagnosisEvaluation &&
    createNewClientSelected &&
    isDbIntakeUpload &&
    !abcIntakeFileUploaded;

  const templateRequired = showTemplateSelector && !selectedTemplate;

  const selectedTypeIsCreatable = reportTypeOptions.some(
    (option) => option.id === selectedReportType && option.enabled,
  );

  const reportCreationDisabled = computeReportCreationDisabled({
    isLearnersLoading,
    clientSelected,
    selectedTypeIsCreatable,
    selectedReportType,
    clientHasSelectedReport,
    isDemoOrgMode,
    abcIntakeFileRequired,
    bhtiaFileIsProcessing,
    templateRequired,
    integratedButMissingLink,
    isReportCreating,
  });

  // PROA-3064: Continue was disabled only while createReport was pending, so on the DE intake
  // branch a second click during file validation created a second report. The guard flips
  // synchronously and holds until the create finishes; after a create, clicks are ignored until
  // the dialog reopens, which matters where each run makes a new sparse learner and the
  // server's repeat check cannot match.
  const handleContinue = useInFlightOnce(async () => {
    if (reportCreatedRef.current) return;
    if (isEhrScraperUser) {
      setIsClientImportedFromEHR(true);
    }
    const action = resolveContinueAction({
      selectedReportType,
      createNewClientSelected,
      abcIntakeLearnerId,
      abcIntakeLearnerFileId,
      bhtiaLearnerId,
      bhtiaLearnerFileId,
      iaIntakeMode,
      dbSinglepageBulkupload: !!flags["db-singlepage-bulkupload"],
    });
    const created = await executeContinueAction(action, {
      validateLearnerFile: (learnerFileId) => startLearnerFileValidation.mutateAsync({ learnerFileId }),
      createReportObject,
    });
    reportCreatedRef.current = created;
  });

  return (
    <Dialog
      title={dialogTitle}
      open={isNewReportDialogOpen}
      setOpen={setNewReportDialogOpen}
      primaryButton={undefined}
      secondaryButton={undefined}
      size={"xl"}
      overflowBehavior="show"
    >
      <DefaultDialogContent
        isEhrIntegrationUser={isEhrIntegrationUser}
        isImporting={isImporting}
        checkLoginStateBeforeClientListImport={checkLoginStateBeforeClientListImport}
        isCRDataWarehouse={isCRDataWarehouse}
        ehrConnectionStatus={ehrConnectionStatus}
        isOrgUsernamePassword={isOrgUsernamePassword}
        isEhrSyncCleared={isEhrSyncCleared}
        createNewClientSelected={createNewClientSelected}
        selectedClientId={selectedClientId}
        onSelectClientId={handleClientSelect}
        onIntegratedButMissingLinkChange={setIntegratedButMissingLink}
        onCreateNewClient={handleCreateNewClient}
        showCardList={!!flags["reassessment-builder"] || !!flags["ia-clinical-merge"]}
        enabledReportTypeOptions={reportTypeOptions.filter((option) => option.enabled)}
        selectedReportType={selectedReportType}
        onReportTypeSelect={handleReportTypeSelect}
        isClientReportLoading={isClientReportLoading}
        clientHasSelectedReport={clientHasSelectedReport}
        isDemoOrgMode={isDemoOrgMode}
        goToReport={goToReport}
        showTemplateSelector={showTemplateSelector}
        templatesLoading={templatesLoading}
        templateSelections={templateSelections}
        selectedTemplate={selectedTemplate}
        setSelectedTemplate={setSelectedTemplate}
      />
      {isEHRLogin ? (
        <EHRLoginDialog
          open={isEHRLogin}
          setOpen={setIsEHRLogin}
          setSuccessLogin={continueToClientListImportAfterLogin}
        />
      ) : null}
      <div className="flex w-full items-center justify-end gap-x-4 pt-8 pb-2">
        <Button appearance="secondary" text="Cancel" onClick={handleSecondaryButtonClick} />
        <Tooltip
          text="Your uploaded file is still being processed. Please wait for parsing to complete."
          showInfoIcon={false}
          disabled={!abcIntakeFileIsProcessing && !bhtiaFileIsProcessing}
        >
          <Button
            appearance="primary"
            disabled={reportCreationDisabled}
            text={isReportCreating ? "Creating..." : "Continue"}
            onClick={handleContinue}
          />
        </Tooltip>
      </div>
    </Dialog>
  );
};
